import { SATOSHIS } from 'lib/bsv-util'

// The daily money rule, shared by the daily-report embeds, the daily
// earnings card twin and their generated share cards: sats up to a dime
// of value, dollars above it. Pure helpers only (unit-tested under node);
// the live-rate hook is useDailyMoney.ts. Sats never carry a "sats" word — the unit is
// the bolt glyph (SatsIcon in-app, the bolt asset on cards, the ⚡ emoji in
// plain text). Mirrors SocialCards::AmountFormatter (daily_money_parts /
// earnings_display) so what the embed shows is what the card says.
export const DAILY_USD_THRESHOLD = 0.1
export const SATS_MARKER = '⚡'

export type DailyMoneyUnit = 'sats' | 'usd'
export type DailyMoney = { text: string; unit: DailyMoneyUnit }

// Mirrors AmountFormatter.format_usd: coarse rounding so cached cards stay
// stable under exchange-rate drift, and the embed agrees with them.
export function formatDailyUsd(usd: number): string {
    if (usd < 10) return `$${usd.toFixed(2)}`
    if (usd < 1000) {
        const rounded = usd < 100 ? Math.round(usd) : Math.round(usd / 10) * 10
        return `$${rounded}`
    }
    const thousands = Math.round(usd / 100) / 10
    return `$${thousands.toFixed(1).replace(/\.0$/, '')}k`
}

export function formatDailyMoney(
    sats: number | string | null | undefined,
    exchangeRate: number | null | undefined,
): DailyMoney {
    const value = Math.round(Number(sats || 0)) || 0
    const usd = exchangeRate && exchangeRate > 0 ? (value / SATOSHIS) * exchangeRate : null
    if (usd == null || usd <= DAILY_USD_THRESHOLD) {
        return { text: value.toLocaleString('en-US'), unit: 'sats' }
    }
    return { text: formatDailyUsd(usd), unit: 'usd' }
}

export function bsvToSats(bsv: number | null | undefined): number {
    const value = Number(bsv || 0)
    return Number.isFinite(value) ? Math.round(value * SATOSHIS) : 0
}

// Plain-text form ("⚡1,234" / "$4.20") for tooltips and captions.
export function dailyMoneyText(money: DailyMoney): string {
    return money.unit === 'sats' ? `${SATS_MARKER}${money.text}` : money.text
}

// Splits a server amount_display ("⚡125k" / "$4.20") so the in-app card
// twin can draw the bolt glyph in place of the emoji. Strings without the
// marker (dollars, or cards minted before the marker) render as-is.
export function parseDailyAmountDisplay(display: unknown): DailyMoney | null {
    if (typeof display !== 'string' || !display) return null
    if (display.startsWith(SATS_MARKER)) {
        return { text: display.slice(SATS_MARKER.length).trim(), unit: 'sats' }
    }
    return { text: display, unit: 'usd' }
}
